/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import css from '@emotion/css/macro'
import { connect } from 'react-redux'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import parsers from '@sportninja/common/reducers/parsers'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import orgActions from '@sportninja/common/actions/org'
import {
  getHockeySport,
  getSoccerSport,
} from '@sportninja/common/selectors/types'
import { useHistory } from 'react-router-dom'

import Page from '../../components/Page'
import Venues from '../../components/Venues'
import FormSheet from '../../components/Form/FormSheet'
import NewButton from '../../components/Page/NewButton'
import {
  feedItemForm,
  orgForm,
  scheduleForm,
  teamForm,
  venueForm,
} from '../../components/Form/form-configs'
import OfficialList, { AddOfficial } from '../../components/List/OfficialList'
import UserList, { AddUser } from '../../components/List/UserList'
import FavouriteButton from '../../components/Page/FavouriteButton'
import { Mobile } from '../../components/Responsive'
import EditButton from '../../components/Page/EditButton'
import ImportCSV from '../../components/ImportCSV'
import BaseActionButton from '../../components/Page/BaseActionButton'
import FileDownload from '../../components/FileDownload'
import EntityFormDelete from '../../components/Form/EntityFormDelete'
import OrgList from '../Orgs/OrgList'
import ScheduleList from '../Schedules/ScheduleList'
import TeamList from '../Teams/TeamList'
import { useReadItem, getBreadcrumbs, useStoreRecent } from '../utils'
import Home from '../Home'
import EditEntitySubTitle from '../EditEntitySubTitle'
import ImportTeamRoster from '../../components/ImportCSV/ImportTeamRoster'
import NewReports from '../../components/NewReports'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { NewModal } from 'src/components/NewModal'
import { NewLocationAddressSelector } from 'src/components/NewLocationAddressSelector'
import Icon from 'src/components/Icon'
import colors from '@sportninja/common/constants/appColors'
import LoadingSpinner from 'src/components/LoadingSpinner'
import { useMobxStore } from 'src/state'
import { observer } from 'mobx-react-lite'

const Organization = observer(({
  createVenue,
  hockeySportId,
  soccerSportId,
  match,
  isInFrame,
  soccerSport,
  hockeySport,
}) => {
  const history = useHistory()
  const {
    loading,
    error,
    item,
    hierarchy,
    permission,
    readItem,
    refreshKey,
    triggerRefresh,
    features,
  } = useReadItem()

  const [isLoadingVenue, setIsLoadingVenue] = useState(false)

  const locationModalRef = useRef(null)

  const ORG_ID = useMemo(() => match.params.id, [match])
  const BASE_ROUTE = `${ROUTES.ORG_ROOT}/${ORG_ID}`
  const IS_PUBLIC = item.is_public
  const syncSalesforce = item?.sync_salesforce || false
  let shouldDisplayMergeButton = false
  if (isCanlan) {
    // Only display merge button for Canlan if the org is Canlan root
    if (permission?.update && ORG_ID === 'H9YZBLskm0BLnlNU') {
      shouldDisplayMergeButton = true
    }
  } else {
    if (permission?.update) {
      shouldDisplayMergeButton = true
    }
  }
  const {me: { account, fetchAccount }} = useMobxStore()

  useStoreRecent(ENTITY_TYPES.org, item, error, ORG_ID, isInFrame)

  useEffect(() => {
    readItem(ENTITY_TYPES.org, ORG_ID)
  }, [ORG_ID])

  const onEditOrg = async (...args) => {
    const [values] = args
    const { imageData, ...form } = values

    if (form?.delete_image) {
      await req(`/organizations/${item.id}/image`, {
        method: 'DELETE',
      })
    }

    const body = JSON.stringify(form)
    const response = await req(`/organizations/${item.id}`, {
      method: 'PUT',
      body,
    })
    if (imageData) {
      await req(`/organizations/${item.id}/image`, {
        method: 'POST',
        body: imageData,
      })
    }
    await readItem(ENTITY_TYPES.org, ORG_ID)

    if (item.id === account?.organization?.url.split('/').pop()) {
      fetchAccount(account?.uid)
    }
    return response
  }

  const onAddOrg = async (...args) => {
    const [values] = args
    const { imageData, ...form } = values

    // Duplicate name_full since API requires it
    form.parent_id = ORG_ID
    form.name = form.name_full
    form.can_contain_organizations = true
    form.can_contain_teams = true

    if (typeof form.is_public === 'undefined') {
      form.is_public = IS_PUBLIC
    }

    const body = JSON.stringify(form)
    const response = await req('/organizations', {
      method: 'POST',
      body,
    })

    if (imageData) {
      await req(`/organizations/${response.data.id}/image`, {
        method: 'POST',
        body: imageData,
      })
    }

    triggerRefresh()
    return response
  }

  const onAddSchedule = async (...args) => {
    const [values] = args
    const {
      imageData,
      season_currency,
      season_type,
      tax_rate,
      online_registration_open,
      is_public,
      ...form
    } = values

    if (form.sport_type) {
      if (form.sport_type === 'Ice Hockey') {
        form.sport_id = hockeySportId
      }
      if (form.sport_type === 'Soccer') {
        form.sport_id = soccerSportId
      }
    } else {
      form.sport_id = hockeySportId
    }

    if (typeof form.is_public === 'undefined') {
      form.is_public = item.is_public
    }
    form.name = form.name_full
    form.settings = {
      season_currency,
      season_type,
      tax_rate,
      online_registration_open,
    }

    const body = JSON.stringify(form)
    const response = await req(`/organizations/${ORG_ID}/schedules`, {
      method: 'POST',
      body,
    })

    if (imageData) {
      await req(`/schedules/${response.data.id}/image`, {
        method: 'POST',
        body: imageData,
      })
    }

    triggerRefresh()
    return response
  }

  const onAddTeam = async (...args) => {
    const [values] = args
    const { imageData, ...form } = values

    form.organization_id = ORG_ID
    form.sport_id = hockeySportId
    form.name = form.name_full

    if (form.sport_type) {
      if (form.sport_type === 'Ice Hockey') {
        form.sport_id = hockeySportId
      }
      if (form.sport_type === 'Soccer') {
        form.sport_id = soccerSportId
      }
    } else {
      form.sport_id = hockeySportId
    }

    if (typeof form.is_public === 'undefined') {
      form.is_public = IS_PUBLIC
    }

    const body = JSON.stringify(form)
    const response = await req('/teams', {
      method: 'POST',
      body,
    })

    if (imageData) {
      await req(`/teams/${response.data.id}/image`, {
        method: 'POST',
        body: imageData,
      })
    }

    triggerRefresh()
    return response
  }

  // const onAddVenue = useCallback(async (data) => {
  //   setIsLoadingVenue(true)

  //   const body = {
  //     address_line1: data?.args?.address_line1,
  //     address_line2: data?.args?.address_line2,
  //     city: data?.args?.city,
  //     postcode: data?.args?.postcode,
  //     country: data?.args?.country,
  //     state: data?.args?.state,
  //     name: data?.args?.name,
  //     timezone: data?.args?.timezone,
  //     latitude: data?.args?.latitude,
  //     longitude: data?.args?.longitude,
  //     state_code: data?.args?.state_code,
  //   }

  //   try {
  //     const response = await req(`/organizations/${ORG_ID}/venues`, {
  //       method: 'POST',
  //       body: JSON.stringify(body),
  //     })

  //     if (response?.data?.id) {
  //       for (let index = 0; index < args?.facilities?.length; index++) {
  //         const facility = args?.facilities[index]

  //         const facilityBody = {
  //           name: facility?.name,
  //         }

  //         await req(
  //           `/organizations/${ORG_ID}/venues/${response?.data?.id}/facilities`,
  //           {
  //             method: 'POST',
  //             body: JSON.stringify(facilityBody),
  //           }
  //         )
  //       }
  //     }
  //   } catch (error) {
  //     console.log('ERROR', error)
  //   } finally {
  //     locationModalRef.current.closeModal()
  //     setIsLoadingVenue(false)
  //     triggerRefresh()
  //   }
  // }, [])

  const onAddVenue = async (...args) => {
    const response = await createVenue(ORG_ID, ...args)
    triggerRefresh()
    return response
  }

  const titleActions = item?.id
    ? [
        <FavouriteButton
          key='favourite'
          initialState={item.is_favorite}
          entityType={ENTITY_TYPES.org}
          entityId={ORG_ID}
        />,
        permission.update && (
          <FormSheet
            customDeleteModal={
              permission.delete && (
                <EntityFormDelete
                  entityType={ENTITY_TYPES.org}
                  itemId={ORG_ID}
                  title={`Delete ${item.name_full}`}
                  type={t('common:organization')}
                  onComplete={() => {
                    history.push(`${ROUTES.ORGS.ROOT}`)
                  }}
                />
              )
            }
            key='edit'
            Button={({ onClick }) => (
              <EditButton onClick={onClick} title={item?.name_full} />
            )}
            form={orgForm(parsers[ENTITY_TYPES.org](item), false, false)}
            label={t('Web:editOrganization')}
            onSubmit={onEditOrg}
            title={t('Web:editOrganization')}
            subTitle={
              <div>
                <EditEntitySubTitle id={ORG_ID} entityType={ENTITY_TYPES.org} />
                {isCanlan && syncSalesforce && item?.sf_id ? (
                  <EditEntitySubTitle
                    id={item?.sf_id}
                    entityType={ENTITY_TYPES.salesforce}
                  />
                ) : null}
              </div>
            }
          />
        ),
      ]
    : []

  const baseActions = [
    shouldDisplayMergeButton && (
      <BaseActionButton
        key='merge'
        name='code-merge'
        title={t('Web:mergePlayers')}
        onClick={() => {
          history.push(ROUTES.MERGE_PLAYERS, {
            organizationName: item?.name_full,
            organizationId: ORG_ID,
          })
        }}
      />
    ),
  ]

  const pages = [
    item.can_contain_organizations && {
      to: `${ROUTES.ORG_ROOT}/${ORG_ID}/children`,
      text: t('Web:subOrg', { count: 2 }),
      component: (
        <OrgList
          refreshKey={refreshKey}
          slug={`/organizations/${ORG_ID}/children`}
        />
      ),
      actions: [
        permission.admin && (
          <FormSheet
            key='add'
            Button={({ onClick }) => <NewButton onClick={onClick} />}
            form={orgForm({ isParentPublic: IS_PUBLIC }, true, false)}
            label={t('Web:createOrg')}
            onSubmit={onAddOrg}
            title={t('Web:createANewSubOrganization')}
          />
        ),
      ],
    },
    item.can_contain_schedules && {
      to: `${ROUTES.ORG_ROOT}/${ORG_ID}/schedules`,
      text: t('common:competition', { count: 2 }),
      component: (
        <ScheduleList
          refreshKey={refreshKey}
          accountTypeId={item?.account_subscription_type_id}
          parentId={ORG_ID}
          slug={`/organizations/${ORG_ID}/schedules`}
          defaultSort='starts_at'
          defaultDirection='desc'
          syncSalesforce={syncSalesforce}
          soccerSport={soccerSport}
          hockeySport={hockeySport}
          orgFeatures={features}
        />
      ),
      actions: [
        permission.admin && (
          <FormSheet
            key='add-schedule'
            Button={({ onClick }) => <NewButton onClick={onClick} />}
            form={scheduleForm(
              { isParentPublic: IS_PUBLIC },
              true,
              syncSalesforce,
              hockeySport?.id
            )}
            label={t('Web:createCompetition')}
            onSubmit={onAddSchedule}
            title={t('Web:createANewCompetition')}
          />
        ),
      ],
    },
    item.can_contain_teams && {
      to: `${ROUTES.ORG_ROOT}/${ORG_ID}/teams`,
      text: t('common:team', { count: 2 }),
      component: (
        <TeamList
          refreshKey={refreshKey}
          parentId={ORG_ID}
          slug={`/organizations/${ORG_ID}/teams`}
          soccerSport={soccerSport}
          hockeySport={hockeySport}
        />
      ),
      actions: [
        <FileDownload
          key='download-teams'
          button={(loading, onClick) => {
            return (
              <BaseActionButton
                loading={loading}
                onClick={onClick}
                name='download'
                title={t('common:exportTeams')}
              />
            )
          }}
          url={`/organizations/${ORG_ID}/teams/export/csv`}
          fileName={item?.name_full}
        />,
        permission.update && (
          <ImportCSV
            key='import-teams'
            button={(label, onClick) => {
              return (
                <BaseActionButton
                  name='upload'
                  title={label}
                  onClick={onClick}
                />
              )
            }}
            entityType={ENTITY_TYPES.org}
            id={ORG_ID}
            importType={ENTITY_TYPES.team}
            onSuccess={triggerRefresh}
          />
        ),
        permission.update && (
          <ImportTeamRoster
            key='import-roster'
            teamName={item.name_full}
            onSuccess={triggerRefresh}
            orgId={ORG_ID}
          />
        ),
        permission.update && (
          <FormSheet
            key='add-team'
            Button={({ onClick }) => <NewButton onClick={onClick} />}
            form={teamForm(
              { isParentPublic: IS_PUBLIC, permission },
              true,
              hockeySportId
            )}
            label={t('Web:createTeam')}
            onSubmit={onAddTeam}
            title={t('Web:createANewTeam')}
          />
        ),
      ],
    },
    item?.can_contain_locations && {
      to: `${ROUTES.ORG_ROOT}/${ORG_ID}/locations`,
      text: t('common:location', { count: 2 }),
      component: (
        <Venues
          entityType={ENTITY_TYPES.org}
          id={ORG_ID}
          slug={`/organizations/${ORG_ID}/venues`}
          refreshKey={refreshKey}
          triggerRefresh={triggerRefresh}
          parentPermission={permission}
        />
      ),
      actions: [
        permission.admin && (
          <FormSheet
            key='add-venue'
            Button={({ onClick }) => <NewButton onClick={onClick} />}
            form={venueForm({}, ORG_ID, ENTITY_TYPES.org)}
            label={t('Web:createLoc')}
            onSubmit={onAddVenue}
            title={t('Web:createANewLocation')}
          />
          // <NewButton onClick={() => locationModalRef.current.openModal()} />
        ),
      ],
    },
    features?.announcements && {
      to: `${ROUTES.ORG_ROOT}/${ORG_ID}/feed`,
      text: t('common:feed'),
      component: (
        <Home
          isEntityFeed
          entity={item}
          entityType={ENTITY_TYPES.org}
          entityId={ORG_ID}
          refreshKey={refreshKey}
          triggerRefresh={triggerRefresh}
          hideSidebar={!permission.update}
        />
      ),
      actions: [
        permission.admin && (
          <Mobile key='add-post'>
            <FormSheet
              label={t('Web:createANewPost')}
              title={t('Web:createANewPost')}
              form={feedItemForm({}, false)}
              onSubmit={async ({ imageData, ...form }) => {
                const body = JSON.stringify(form)
                const response = await req(
                  `/organizations/${ORG_ID}/social-feed`,
                  {
                    method: 'POST',
                    body,
                  }
                )
                if (imageData) {
                  await req(`/social-feed/${response.data.id}/image`, {
                    method: 'POST',
                    body: imageData,
                  })
                }
                triggerRefresh()
              }}
              Button={({ onClick }) => <NewButton onClick={onClick} />}
            />
          </Mobile>
        ),
      ],
    },
    {
      to: `${ROUTES.ORG_ROOT}/${ORG_ID}/reports`,
      text: 'Reports',
      component: (
        <NewReports
          entity={item}
          entityType={ENTITY_TYPES.org}
          entityId={ORG_ID}
        />
      ),
    },
    !isInFrame &&
      permission.admin && {
        to: `${ROUTES.ORG_ROOT}/${ORG_ID}/officials`,
        text: t('common:official', { count: 2 }),
        component: (
          <OfficialList
            key='officials'
            entityType={ENTITY_TYPES.org}
            entityId={ORG_ID}
            slug={`/organizations/${ORG_ID}/officials`}
            refreshKey={refreshKey}
          />
        ),
        privatePage: true,
        actions: (isCanlan || features?.rolesPermissions) && [
          <AddOfficial
            key='add-official'
            entityType={ENTITY_TYPES.org}
            entityId={ORG_ID}
            permission={permission}
            onComplete={triggerRefresh}
            isOrg={true}
          />,
        ],
      },
    !isInFrame &&
      permission.admin && {
        to: `${ROUTES.ORG_ROOT}/${ORG_ID}/users`,
        text: t('common:user', { count: 2 }),
        component: (
          <UserList
            key='users'
            entityType={ENTITY_TYPES.org}
            entityId={ORG_ID}
            slug={`/organizations/${ORG_ID}/permissions`}
            refreshKey={refreshKey}
          />
        ),
        privatePage: true,
        actions: (isCanlan || features?.rolesPermissions) && [
          <AddUser
            key='add-user'
            entityType={ENTITY_TYPES.org}
            entityId={ORG_ID}
            permission={permission}
            onComplete={triggerRefresh}
          />,
        ],
      },
  ]

  return (
    <>
      <Page
        error={error}
        loading={loading}
        baseCrumb={ROUTES.ORGS.ROOT}
        baseRoute={BASE_ROUTE}
        baseTitle={t('common:organization', { count: 2 })}
        breadcrumbs={getBreadcrumbs(ROUTES.ORG_ROOT, item, hierarchy)}
        baseActions={baseActions}
        pages={pages}
        isPrivate={!IS_PUBLIC}
        title={item.name_full}
        titleActions={titleActions}
      />
      {/* <NewModal ref={locationModalRef} shouldCloseOnOverlayClick={false}>
        <div
          css={css`
            width: 40vw;
            background: no-repeat fixed linear-gradient(#282e38, #181a1d);
            max-height: 80vh;
            border-radius: 8px;
            padding: 20px;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            transition: all 0.1s ease-in-out;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-bottom: 16px;
              border-bottom: 1px solid ${colors.SOFT_STEEL};
              margin-bottom: 16px;
            `}
          >
            <h1
              css={css`
                font-size: 36px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'Rift, Arial, Helvetica, sans-serif'};
                font-weight: 700;
              `}
            >
              {t('Web:createANewLocation')}
            </h1>
            <button
              onClick={() => locationModalRef.current.closeModal()}
              // disabled={isLoadingVenue}
            >
              <Icon name='times' color={colors.ATTENDANCE_GRAY} fontSize={20} />
            </button>
          </div>
          <div
            css={css`
              flex: 1;
              flex-grow: 1;
              display: flex;
              transition: all 0.1s ease-in-out;
            `}
          >
            {isLoadingVenue ? (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  flex: 1;
                `}
              >
                <LoadingSpinner />
              </div>
            ) : (
              <NewLocationAddressSelector
                onCancel={() => locationModalRef.current.closeModal()}
                onSubmit={onAddVenue}
              />
            )}
          </div>
        </div>
      </NewModal> */}
    </>
  )
})

const mapStateToProps = (state) => {
  return {
    hockeySportId: getHockeySport(state)?.id,
    soccerSportId: getSoccerSport(state)?.id,
    hockeySport: getHockeySport(state),
    soccerSport: getSoccerSport(state),
    isInFrame: state.auth.inFrame,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    createVenue: bindActionToPromise(
      dispatch,
      orgActions.venues.create.request
    ),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Organization)
